import { isC3Experience } from "../../../shared/util/C3Utils";

define((require) => {
    "use strict";

    const _                             = require("underscore");
    const $                             = require("jquery");
    const Backbone                      = require("backbone");
    const BreakpointGroup               = require("shared/ui/breakpoint/BreakpointGroup");
    const Configuration                 = require("shared/services/Configuration");
    const CommentCollection             = require("ui/comments/CommentCollection");
    const EditorControllerMixin         = require("ui/editor/EditorControllerMixin");
    const EditorEvents                  = require("shared/services/EditorEvents");
    const EditorNavBarView              = require("ui/editor/EditorNavBarView");
    const EditorView                    = require("ui/editor/EditorView");
    const EditorViewModel               = require("editor/ui/experience/editor/EditorViewModel");
    const ExperienceModel               = require("shared/ui/experience/ExperienceModel");
    const ExperienceURLProvider         = require("ui/experience/ExperienceURLProvider");
    const GroupCollection               = require("shared/ui/groups/GroupCollection");
    const LayerEnum                     = require("ui/editor/LayerEnum");
    const LoggedIn                      = require("shared/security/LoggedIn");
    const LoggedInUserState             = require("shared/ui/user/LoggedInUserState");
    const ImageLibrarySortingEnum       = require("ui/imageLibrary/ImageLibrarySortingEnum");
    const ImageLibrarySortingProvider   = require("ui/imageLibrary/ImageLibrarySortingProvider");
    const ImageLibrarySourceEnum        = require("shared/data/images/ImageLibrarySourceEnum");
    const {Logger}                      = require("shared/util/Logger");
    const Marionette                    = require("marionette");
    const NotificationBar               = require("editor/ui/notificationbar/NotificationBar");
    const Promise                       = require("shared/util/Promise");
    const PublishableState              = require("util/PublishableState");
    const Scene                         = require("shared/ui/experience/Scene");
    const Storage                       = require("shared/services/Storage");
    const TemplateModel                 = require("ui/experience/TemplateModel");

    const logger = new Logger("EditorController");

    const DEFAULT_WIDTH  = 900;
    const DEFAULT_HEIGHT = 400;

    /**
     * Controller for the editor.
     *
     * @author Bo Gotthardt
     * @class EditorController
     * @constructor
     *
     * @cfg {Region} region The main view region, where the editor view should be rendered.
     */
    const EditorController = Marionette.Controller.extend({

        mixin: EditorControllerMixin,

        currentExperience: null,

        currentProject: null,

        viewModel: null,

        _editorView: null,

        /**
         * Show the top nav bar view for editor page.
         *
         * @param {GroupCollectiom} groups The current collection of groups.
         *
         * @private
         */
        _showNavBarView(groups) {
            const self = this;
            const newDashboardExperience = isC3Experience();
            // PE-1216: New C3-API doesn't have endpoints for groups, hence we define the group as undefined instead of fetching it
            const group = newDashboardExperience ? undefined : groups.findByAssetId(this.currentExperience.get("id"));

            let promise = Promise.resolved();
            let pushed = null;

            if (group && group instanceof BreakpointGroup) {
                pushed = new BreakpointGroup({id: group.id}, {pushedOnly: true});

                if (group.hasAtLeastState(PublishableState.PUSHED)) {
                    promise = pushed.fetchPushedOnly();
                }
            }

            promise.then(() => {
                const navBarOptions = {
                    model: self.currentExperience,
                    project: self.currentProject,
                    companySettings: LoggedInUserState.get("companySettings"),
                    viewModel: self.viewModel,
                    principal: LoggedIn.principal(),
                    group: group,
                    pushed: pushed,
                    integrationsCollection: LoggedInUserState.get("integrations")
                };

                self.options.navBarRegion.show(new EditorNavBarView(navBarOptions));
            });
        },

        /**
         * @private
         * Will set the current project model. In case the current experience is part of any group, it will check if the group is part of the project.
         * Otherwise, it will check if the individual experience is part of the project.
         * Make sure that this.currentExperience is set before calling this method, otherwise it will blow up.
         * @returns {ProjectModel} the project model, if the current experience is part of any project or if it's part of a group that's part of the project, undefined otherwise.
         */
        _setProject(experienceModel, groups) {
            const experienceId        = experienceModel.get("id");
            const newDashboardExperience = isC3Experience();
            Storage.Local.update(Storage.Keys.NEW_DASHBOARD_EXP, Storage.Keys.NEW_DASHBOARD_EXP, newDashboardExperience);
            // PE-1482: New C3-API doesn't support projects, hence no need to set projects for the same
            if (newDashboardExperience) {
                var link = document.querySelector("link[rel~='icon']");
                if (!link) {
                    link = document.createElement('link');
                    link.rel = 'icon';
                    document.head.appendChild(link);
                }
                link.href = '/images/icons/favicon.png';
                document.title = "Fastr Frontend"
                return;
            }
            const group               = newDashboardExperience ? undefined : groups.findByAssetId(experienceId);
            const projectsCollection  = LoggedInUserState.get("projects");

            if (group) {
                const projectFromGroup = projectsCollection.byGroupId(group.get("id"));
                if (projectFromGroup) {
                    this.currentProject = projectFromGroup;
                } else {
                    this.currentProject = projectsCollection.byExperienceId(experienceId);
                }
            } else {
                this.currentProject = projectsCollection.byExperienceId(experienceId);
            }
        },

        /**
         * When editing a group of variants, each variant can have different number of scenes,
         * Switching between variants can result in accessing invalid scene number.
         *
         * @param sceneNumber   The current scene number being edited
         * @returns {*}     The scene number if valid, otherwise a new scene number
         */
        validateSceneNumber(sceneNumber) {
            const total = this.currentExperience.getNumberOfScenes();

            if (total < sceneNumber || sceneNumber < 1) {
                EditorController._navigateToExperienceEditURL(this.currentExperience, total, false);
                return total;
            }

            return sceneNumber;
        },

        // When an experience is resized we need to run through every widget and update its
        // size and position to counter the resizing (to avoid them growing/shrinking together
        // with the experience.
        updateWidgetsSizeAndPosition() {
            const experience = this.currentExperience;
            const widgets = experience.getAllWidgets();
            const scaleX = experience._previousAttributes.width / experience.get("width");
            const scaleY = experience._previousAttributes.height / experience.get("height");

            _.each(widgets, (widget) => {
                const bounds = {
                    left: widget.get("left") * scaleX,
                    top: widget.get("top") * scaleY
                };

                if (widget.has("width")) {
                    bounds.width = widget.get("width") * scaleX;
                }

                if (widget.has("height")) {
                    bounds.height = widget.get("height") * scaleY;
                }

                widget.set(bounds);
            });
        },

        _getContentControlBetaFlag() {
            try {
                const companyId = LoggedInUserState.attributes.company.id,
                    settingName = "contentControlBeta",
                    endpointUrl = `${Configuration.creatorSettingApiBaseUrl}/v1/settings/${companyId}?name=${settingName}`,
                    token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token"),
                    bearerToken = `Bearer ${token}`;
                const options = {
                    method: "GET",
                    url: endpointUrl,
                    headers: {
                        "Authorization": bearerToken
                    },
                    success: "success",
                    dataType: "json",
                    async: false
                };

                const requestObject = $.ajax(options);
                const response = requestObject.responseJSON || null;

                Storage.Local.update(Storage.Keys.CONTENT_CONTROL_BETA, Storage.Keys.CONTENT_CONTROL_BETA, response.contentControlBeta);

                return response.contentControlBeta;
            } catch (error) {
                return false;
            }
        },

        /**
         * Edit an existing experience with the specified id at the given scene index.
         *
         * @param {String} id The experience id.
         * @param {Number|String} sceneNumber The scene number or special scene name.
         * @param {String} [layerName=LayerEnum.SCENE.name] The value of the Layer Enum to select for the user.
         *
         * @return {Promise} A promise that resolves with the fetched <tt>ExperienceModel</tt>.
         */
        editExperience(id, sceneNumber, layerName) {
            const deferred = new $.Deferred();
            const self = this;
            let experienceCommentsJSON;
            let groupCollection;
            const newDashboardExperience = isC3Experience();
            const contentControlBetaFlagEnabled = this._getContentControlBetaFlag();

            this._fetchExperienceAndFonts(id)
                .then((model) => {

                    // PE-1212: IF the user has contentControlBeta flag disabled
                    // AND they try to access a C3 experience, redirect to root with error
                    if (!contentControlBetaFlagEnabled && newDashboardExperience) {
                        window.location.href = "#";
                        NotificationBar.error("Unable to load the experience. Please try again.");
                        return;
                    }

                    // Let the model attempt to get its native code payload.
                    model.getNativeCode("EditorLoad");
                    self.currentExperience = model;

                    // The experience might be part of a group, we need to lookup any potential
                    // group in order to render group specific UI for navigation bar and editor.
                    // PE-1216: New C3-API doesn't have endpoints for groups, hence we define the group as undefined instead of fetching it
                    const groupCollectionResult = newDashboardExperience ? [] : GroupCollection.byAssetId(id);
                    return groupCollectionResult;
                })
                .then((groups) => {
                    groupCollection = groups;
                    self._setProject(self.currentExperience, groups);
                    // PE-1216: New C3-API doesn't have endpoints for comments, hence we define the group as undefined instead of fetching it
                    const commentCollectionResult = newDashboardExperience ? [] : CommentCollection.fetchCommentsFor(id);
                    return commentCollectionResult;
                })
                .then((commentsJSON) => {
                    experienceCommentsJSON = commentsJSON;
                    // Fetch template model, if needed...
                    return LoggedInUserState.get("companySettings").get("templateSharingEnabled")
                        ? TemplateModel.fromExperience(id)
                        : Promise.resolved(null);
                })
                .then((templateModel) => {
                    // Enable save logic
                    self._listenForChanges(self.currentExperience);

                    sceneNumber = self.validateSceneNumber(sceneNumber);

                    const imageViewedEventsEnabled = LoggedInUserState.get("companySettings").get("imageViewedEventsEnabled");

                    const creatorSortingFilter = !imageViewedEventsEnabled ? ImageLibrarySortingEnum.MOST_VIEWED : ImageLibrarySortingEnum.ALPHABETICAL;

                    const providerOptions = {
                        sources: [
                            {source: ImageLibrarySourceEnum.CREATOR, sorting: ImageLibrarySortingEnum.without(creatorSortingFilter)}
                        ]
                    };

                    if (LoggedInUserState.get("integrations").isScene7IntegrationAuthorized()) {
                        providerOptions.sources.push({
                            source: ImageLibrarySourceEnum.SCENE7,
                            sorting: ImageLibrarySortingEnum.without(ImageLibrarySortingEnum.MOST_VIEWED, ImageLibrarySortingEnum.MOST_USED)
                        });
                    }

                    const imageLibrarySorting = ImageLibrarySortingProvider.provide(providerOptions);

                    self.viewModel = new EditorViewModel({
                        experience: self.currentExperience,
                        sceneNumber: sceneNumber,
                        project: self.currentProject,
                        templateModel,
                        experienceCommentsJSON: experienceCommentsJSON,
                        imageLibrarySorting,
                        imageTaggingEnabled: LoggedInUserState.get("integrations").imageRekognitionIntegrationsEnabled(),
                        inventoryEnabled: LoggedInUserState.get("integrations").isInventoryEnabled(),
                        imageViewedEventsEnabled,
                        currentLayer: LayerEnum.lookup(layerName, LayerEnum.SCENE)
                    });

                    self._editorView = new EditorView({
                        model: self.viewModel,
                        project: self.currentProject,
                        groups: groupCollection,
                        prototypeWidgetCollection: LoggedInUserState.get("prototypeWidgetCollection")
                    });

                    deferred.resolve({experience: self.currentExperience, viewModel: self.viewModel});

                    self._showNavBarView(groupCollection);

                    self.listenTo(self.currentExperience, "change:height change:width", self.updateWidgetsSizeAndPosition);
                    self.listenTo(self.viewModel, "change:sceneNumber", () => {
                        const experienceEditURL = ExperienceURLProvider.provide(
                            self.viewModel.get("experience").get("id"),
                            ExperienceURLProvider.ACTION.EDIT,
                            self.viewModel.get("sceneNumber")
                        );
                        Backbone.history.navigate(experienceEditURL, {replace: true});
                    });

                    // Display the editor view
                    self.options.region.show(self._editorView);
                })
                .fail((msg) => {
                    deferred.reject(msg);
                    logger.error(msg);
                    Backbone.history.navigate("", true);
                });

            return deferred;
        },

        _listenForChanges(experience) {
            const save = (delay) => {
                return function() {
                    // Ensure the change wasn't triggered by a server update or a computed property. We always loop
                    // backwards in the arguments, since options like {triggeredByServerUpdate: true, ..} will
                    // one of the last arguments (always last?)...
                    let i = arguments.length,
                        arg;

                    while (i--) {
                        arg = arguments[i];

                        if (arg.triggeredByServerUpdate || arg.computedProperty) {
                            return;
                        }
                    }

                    experience.saveLater(delay);
                };
            };

            // Defer saving until a bit later since more simple changes may come in the very near future...
            this.listenTo(experience, "change nested-change", save()); // 2s delay
            this.listenTo(experience,  "behaviorSaved", () => {
                NotificationBar.open("Behavior saved successfully", {closeDelay: 5000});
            });

            // On structural changes, save now (still queued), as the logic to handle variants will wait until
            // stable variants, i.e. no need to wait the default delay. We use 1ms delay only, to ensure
            // scene indexes have be recomputed at the frontend before submit or the backend will complain!
            this.listenTo(experience, "add:scenes remove:scenes", save(1));
        },

        onClose() {
            this.stopListening();

            // Clear all notifications about possible concurrent modification...
            NotificationBar.close({tag: "concurrent:", clear: "tag"});
        }

    }, {
        injection: {
            parameters: ["errorLogger"],
            asOptions: true
        },

        /**
         * Create a new experience and start editing it. Because this method does not hold any state,
         * we use it as a static method.
         *
         * @param {String} [projectId] - Optional. The project id in which to add the newly
         *                                  created experience.
         */
        editNewExperience(projectId) {
            const project = projectId ? LoggedInUserState.get("projects").get(projectId) : null;
            const size = Storage.Local.get(Storage.Keys.EDITOR, "experienceSize", {
                width: DEFAULT_WIDTH,
                height: DEFAULT_HEIGHT
            });

            const principal = LoggedIn.principal(),
                model = new ExperienceModel({
                    createdBy: principal.get("userId"),
                    companyId: principal.get("companyId"),
                    width: size.width,
                    height: size.height,
                    // This is currently part of the contract with the backend - an experience needs at least 1 scene...
                    scenes: [new Scene({sceneNumber: 1})]
                });

            return model.save()
                .then(() => {
                    const eventProperties = {
                        experienceId: model.get("id"),
                        experienceVersion: model.get("version"),
                        experiencePushedVersion: model.get("pushedVersion"),
                        projectId: project && project.get("id"),
                        triggerOrigin: project ? "CreateNewExperienceInProjectArea" : "CreateNewExperienceArea"
                    };

                    const eventName = EditorEvents.constants.CREATE_EXPERIENCE;
                    // Attach the experience to the project
                    if (project) {
                        return project.attach(model)
                            .then(() => {
                                EditorEvents.trigger(eventName, eventProperties);
                                EditorController._navigateToExperienceEditURL(model);
                            });
                    }

                    EditorEvents.trigger(eventName, eventProperties);
                    EditorController._navigateToExperienceEditURL(model);
                });
        },

        /**
         * @private
         *
         * Navigates to experience Edit URL.
         *
         * @param {ExperienceModel} model - Experience model.
         * @param {Number} sceneNumber - Optional, the scene number to navigate to
         * @param {Boolean} trigger - Optional - if it should call the route function.
         */
        _navigateToExperienceEditURL(model, sceneNumber, trigger) {
            var experienceURL = ExperienceURLProvider.provide(model.get("id"), ExperienceURLProvider.ACTION.EDIT, sceneNumber);

            trigger = trigger !== false;
            Backbone.history.navigate(experienceURL, {trigger: trigger, replace: true});
        }
    });

    return EditorController;
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/EditorController.js